// [claude-code 2026-03-09] Added: useMicPermission, useMicArbitration, explicit error state, cancel/interrupt support
// [claude-code 2026-03-23] Replaced SpeechRecognition with getUserMedia + MediaRecorder + Whisper transcription.
//   Added mic device selection and silence-based VAD.
// [claude-code 2026-04-10] Extracted useMicPermission → useMicPermission.ts, useMicArbitration → useMicArbitration.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import { useBackend } from "../lib/backend";
import { isNativeSpeechRecognitionSupported } from "../lib/voice-browser-runtime";
import type { VoiceRuntimeState } from "../types/voice";
import { playVoiceEndSuccessSound } from "../utils/soundAlerts";
import { useMicPermission } from "./useMicPermission";
import { useMicArbitration } from "./useMicArbitration";
import { isVoiceWidgetSandboxPreview } from "./voice-sandbox-mode";
import {
  VOICE_CONVERSATION_STORAGE_KEY,
  VOICE_ENABLED_STORAGE_KEY,
  VOICE_REPLY_AUDIO_STORAGE_KEY,
  safeLocalStorageGet,
  safeLocalStorageSet,
} from "./voice-assistant-storage";
import type { RecordingSession } from "./voice-listening-session";
import { useVoiceAssistantRuntime } from "./useVoiceAssistantRuntime";

export { useMicPermission } from "./useMicPermission";
export { useMicArbitration } from "./useMicArbitration";

interface UseVoiceAssistantOptions {
  /** Called with every Whisper transcript before sendText — used for ER scoring */
  onTranscript?: (text: string) => void;
}

export function useVoiceAssistant(options?: UseVoiceAssistantOptions) {
  const backend = useBackend();
  const { alertConfig, voiceChatPermissionLevel } = useSettings();
  const isSoundEnabled = alertConfig.soundEnabled;

  const [enabled, setEnabledState] = useState(false);
  const [runtimeState, setRuntimeState] = useState<VoiceRuntimeState>("idle");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [lastUserText, setLastUserText] = useState("");
  const [lastAssistantText, setLastAssistantText] = useState("");
  const [hasUserRequestActivity, setHasUserRequestActivity] = useState(false);
  const [replyAudioEnabled, setReplyAudioEnabledState] = useState(
    () => safeLocalStorageGet(VOICE_REPLY_AUDIO_STORAGE_KEY) !== "false",
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const enabledRef = useRef(false);
  const replyAudioEnabledRef = useRef(replyAudioEnabled);
  const busyRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const micReleaseRef = useRef<(() => void) | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micRequestIdRef = useRef(0);
  const errorRecoveryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordingRef = useRef<RecordingSession | null>(null);
  const readySoundPlayedRef = useRef(false);
  const startListeningRef = useRef<() => void>(() => {});
  const onTranscriptRef = useRef(options?.onTranscript);

  const { permission } = useMicPermission();
  const { requestMic } = useMicArbitration();

  onTranscriptRef.current = options?.onTranscript;

  // Supported when the browser can capture audio and either recognize speech
  // natively or fall back to the older recorder path.
  const isSupported = useMemo(
    () =>
      isVoiceWidgetSandboxPreview() ||
      (typeof navigator !== "undefined" &&
        typeof navigator.mediaDevices?.getUserMedia === "function" &&
        (typeof MediaRecorder !== "undefined" ||
          isNativeSpeechRecognitionSupported())),
    [],
  );

  const {
    cancel,
    respondToInfraction,
    sendText,
    setReplyAudioEnabled,
    startListening,
    stopPlayback,
    stopRecording,
    releaseAudioInput,
  } = useVoiceAssistantRuntime({
    backend,
    conversationId,
    permissionLevel: voiceChatPermissionLevel,
    permission,
    isSupported,
    isSoundEnabled,
    audioRef,
    enabledRef,
    replyAudioEnabledRef,
    busyRef,
    abortRef,
    micReleaseRef,
    micStreamRef,
    micRequestIdRef,
    errorRecoveryRef,
    recordingRef,
    readySoundPlayedRef,
    startListeningRef,
    onTranscriptRef,
    requestMic,
    setConversationId,
    setRuntimeState,
    setLastUserText,
    setLastAssistantText,
    setHasUserRequestActivity,
    setReplyAudioEnabledState,
  });

  // Keep ref in sync so sendText can restart listening without circular deps
  startListeningRef.current = startListening;

  // ─── Enable / Disable ──────────────────────────────────────────────────────

  const setEnabled = useCallback(
    (nextEnabled: boolean) => {
      const wasEnabled = enabledRef.current;
      setEnabledState(nextEnabled);
      enabledRef.current = nextEnabled;
      safeLocalStorageSet(
        VOICE_ENABLED_STORAGE_KEY,
        nextEnabled ? "true" : "false",
      );

      if (!nextEnabled) {
        cancel();
        releaseAudioInput();
        if (wasEnabled) playVoiceEndSuccessSound(isSoundEnabled);
        readySoundPlayedRef.current = false;
        setHasUserRequestActivity(false);
        return;
      }

      readySoundPlayedRef.current = false;
      startListening();
    },
    [startListening, cancel, isSoundEnabled, releaseAudioInput],
  );

  const toggleEnabled = useCallback(() => {
    setEnabled(!enabledRef.current);
  }, [setEnabled]);

  const finishCurrentInput = useCallback(() => {
    if (!enabledRef.current || busyRef.current) return;
    stopRecording();
  }, [stopRecording]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    replyAudioEnabledRef.current = replyAudioEnabled;
  }, [replyAudioEnabled]);

  // Restore the conversation, but never reacquire the microphone on load.
  useEffect(() => {
    const savedConversationId = safeLocalStorageGet(
      VOICE_CONVERSATION_STORAGE_KEY,
    );

    if (savedConversationId) {
      setConversationId(savedConversationId);
    }

    safeLocalStorageSet(VOICE_ENABLED_STORAGE_KEY, "false");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-restart listening if state gets stuck
  useEffect(() => {
    if (
      enabled &&
      isSupported &&
      runtimeState === "listening" &&
      !recordingRef.current &&
      !busyRef.current &&
      !isVoiceWidgetSandboxPreview()
    ) {
      startListening();
    }
  }, [enabled, runtimeState, isSupported, startListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      releaseAudioInput();
      stopPlayback();
      if (errorRecoveryRef.current) clearTimeout(errorRecoveryRef.current);
    };
  }, [releaseAudioInput, stopPlayback]);

  return {
    enabled,
    runtimeState,
    conversationId,
    lastUserText,
    lastAssistantText,
    hasUserRequestActivity,
    replyAudioEnabled,
    isSupported,
    micPermission: permission,
    setEnabled,
    setReplyAudioEnabled,
    toggleEnabled,
    finishCurrentInput,
    sendText,
    respondToInfraction,
    cancel,
  };
}
